import {
  ClipboardList,
  FolderKanban,
  Bug,
  ListTree,
  Boxes,
  Plug,
  Rocket,
  Users,
  Bot,
  KanbanSquare,
  BarChart3,
  SlidersHorizontal,
  FileInput,
  Paperclip,
  FileBarChart,
  PlayCircle,
  Download,
  UserCog,
  Route,
  Layers,
  LayoutDashboard,
  MessagesSquare,
  Bell,
  CheckCircle2,
  Video,
  Cpu,
  Database,
  Workflow as WorkflowIcon,
  Link2,
  Clock,
  CalendarClock,
  Wand2,
  Repeat,
} from "lucide-react";
import Header from "../Header/Header";
import Footer from "../Footer/footer";
import SEOHead from "../SEO/SEOHead";
import { PageHero, BetaBadge, HeroShots } from "../common/ui";
import SolutionShowcase from "../common/SolutionShowcase";
import "../../css/SolutionShowcase.css";
import { integrations, IntegrationLogo } from "../common/integrations";
import "../../css/MainPage.css";
import "../../css/Pages.css";

import testCasesShot from "../../../assets/product/test-cases.webp";
import dashboardShot from "../../../assets/product/project-dashboard.webp";
import defectsShot from "../../../assets/product/defects-list.webp";
import requirementsListShot from "../../../assets/product/requirements-list.webp";
import requirementsShot from "../../../assets/product/requirements-generate-ai.webp";
import automationShot from "../../../assets/product/automation-script-ai.webp";
import kanbanShot from "../../../assets/product/task-kanban.webp";
import FrothAiIcon from "../common/FrothAiIcon";

const tabs = [
  {
    id: "qa-test-management",
    label: "QA Test Management",
    icon: ClipboardList,
    title: "Test management with full traceability and reporting",
    image: testCasesShot,
    screenshot: true,
    imageHeight: 700,
    imageAlt: "Test case list with approval status, AI-generated share and automation coverage",
    paragraphs: [
      "Froth TestOps brings every QA activity into one place: test planning, execution, defect tracking, reporting and final approvals. It's built for QA teams, developers and managers, with an interface that keeps even complex testing easy to follow.",
      "With FrothAI, Froth TestOps generates test scenarios and test cases from requirements, scenarios or defects, autofills test case forms, and helps you assess release risk before sign-off.",
    ],
    bullets: [
      "AI test generation and release-risk assessment",
      "Approval, review and sign-off workflows",
      "End-to-end traceability with two-way tool sync",
    ],
    sections: [
      {
        title: "Streamlined test execution and tracking",
        text: [
          "Monitor testing in real time and adapt quickly as requirements change. Froth TestOps makes it simple to execute tests and capture results, so teams cut manual work and speed up the whole testing cycle.",
        ],
      },
      {
        title: "Customizable test case management",
        text: [
          "Every team works differently. Define reusable pre-conditions, modular test cases and custom fields that reflect your Agile or hybrid methods.",
        ],
      },
      {
        title: "Data-driven testing insights",
        text: [
          "Build detailed, customizable reports on test coverage, execution and defects, and use dashboards and FrothAI release-readiness analysis to make better release decisions.",
        ],
      },
    ],
    cards: [
      {
        icon: SlidersHorizontal,
        title: "Customizable fields",
        text: "Add your own fields to projects, builds, modules, requirements, test scenarios, test cases and defects (text, dates, checkboxes, dropdowns and more), each with role-based visibility.",
      },
      {
        icon: Repeat,
        title: "Reusable pre-conditions",
        text: "Write a pre-condition once and reuse it across test cases.",
      },
      {
        icon: Paperclip,
        title: "Field attachments",
        text: "Attach documents or screenshots to tests, requirements and defects for better clarity and documentation.",
      },
      {
        icon: FileBarChart,
        title: "Custom test reports",
        text: "Get the exact insights you need, with report filters for status, severity, coverage and more, saved as templates or scheduled by email.",
      },
      {
        icon: PlayCircle,
        title: "Manual & API-based test execution",
        text: "Execute test cases manually, or automate them, including data-driven REST API tests and multi-step API workflows, and trigger them from your CI pipeline.",
      },
      {
        icon: Download,
        title: "Export made easy",
        text: "Import and export test cases and results as Excel or CSV, quickly and accurately.",
      },
    ],
    faqs: [
      {
        q: "What is a Test Management Tool?",
        a: "Software that helps teams plan, run and track tests. Froth TestOps centralizes testing and reporting in one platform.",
      },
      {
        q: "Why is Test Management Important?",
        a: "It improves quality, speeds up delivery and keeps teams aligned. Froth TestOps makes testing efficient and insightful.",
      },
      {
        q: "What types of Test Management Tools are there?",
        a: "There are manual, automated and hybrid tools. Froth TestOps supports both manual and automated testing for maximum flexibility.",
      },
    ],
  },
  {
    id: "project-management",
    label: "Project Management",
    icon: FolderKanban,
    title: "Testing in Project Management Just Got Easier",
    paragraphs: [
      "Efficient testing is key to delivering projects on time, especially when deadlines are tight and several teams are involved. Froth TestOps builds testing into your project lifecycle with a dedicated project module covering builds, modules, requirements and team members.",
      "Organise release planning and module-wise test cases in one workspace, and sync with Jira, Plane or Azure DevOps so QA leads, developers and business stakeholders work from the same information.",
    ],
    cards: [
      {
        icon: Route,
        title: "End-to-End Testing Made Simple",
        text: "Plan, run and monitor end-to-end testing from planning to release in one clear interface.",
      },
      {
        icon: Layers,
        title: "Built-in Build Management",
        text: "Track planned, active and completed builds, link them to test runs, modules and defects, and see every release on a timeline.",
      },
      {
        icon: Boxes,
        title: "Create and Organize Modules",
        text: "Divide large projects into modules and link test scenarios, test cases and requirements to each one, so testing stays organised and traceable.",
      },
      {
        icon: UserCog,
        title: "Team Management Made Easy",
        text: "Add team members, assign roles and set permissions per project, so everyone knows their responsibilities.",
      },
    ],
    faqs: [
      {
        q: "What are Project Management Platforms?",
        a: "Software that helps teams plan, manage and track tasks, timelines and resources. Froth TestOps delivers project management built for QA, connecting testing with the rest of the delivery process.",
      },
      {
        q: "Does Froth TestOps Support Project Management?",
        a: "Yes. Froth TestOps includes a full project module designed for QA and test coordination. It works like a command centre where you oversee builds, modules, test cycles and user roles, with full control and visibility.",
      },
      {
        q: "Key Features of Project Management in Froth TestOps",
        a: (
          <ul>
            <li>Build & Release Planning (with Release Timeline)</li>
            <li>Integrated Build Management</li>
            <li>Test & Requirement Linking</li>
            <li>Team & Role Assignment</li>
            <li>Defect Traceability</li>
            <li>Real-Time Dashboards & Reports</li>
            <li>Review & Sign-off Workflows</li>
          </ul>
        ),
      },
    ],
  },
  {
    id: "defect-management",
    label: "Defect Management",
    icon: Bug,
    title: "Built for Effective Bug and Defect Tracking",
    image: defectsShot,
    screenshot: true,
    imageHeight: 775,
    imageAlt: "Defect list with severity, priority, status and defect type, above priority and severity breakdowns",
    paragraphs: [
      "Froth TestOps gives modern QA teams a streamlined defect management system. Log and track bugs while you test, link them to test cases, and sync defects two-way with Jira, Plane or Azure DevOps.",
      "FrothAI can turn any defect into regression test cases, so the same bug doesn't come back.",
    ],
    bullets: [
      "Prioritise defects by severity and urgency",
      "Add steps, expected and actual results, browsers, devices and attachments",
      "Keep statuses current with bulk edit and import/export",
      "See the test cases and executions linked to each defect",
    ],
    cards: [
      {
        icon: LayoutDashboard,
        title: "Information-Rich Overview",
        text: "See totals, priority and severity breakdowns for your defects at a glance.",
      },
      {
        icon: FileBarChart,
        title: "Custom Defect Reports",
        text: "Build defect reports tailored to your team, save them as templates and schedule them by email.",
      },
      {
        icon: FrothAiIcon,
        accent: true,
        title: "Tests from Defects",
        text: "Let FrothAI suggest regression test scenarios and test cases from a defect, then review and insert them.",
      },
    ],
    faqs: [
      {
        q: "What is Defect Management?",
        a: "Defect management is the process of logging, tracking and resolving bugs found during testing. Froth TestOps keeps every defect linked to test cases, builds and requirements, with two-way sync to Jira, Plane and Azure DevOps.",
      },
      {
        q: "How does FrothAI help with defects?",
        a: "FrothAI can turn any defect into regression test scenarios and test cases, so the same bug doesn't come back. Review the suggestions and insert only the ones you want.",
      },
      {
        q: "Can I bulk edit, import and export defects?",
        a: "Yes. Keep statuses current with bulk edit, and move defects in and out of Excel or CSV quickly and accurately.",
      },
    ],
  },
  {
    id: "requirement-management",
    label: "Requirement Management",
    icon: ListTree,
    title: "Effective Requirement Management for Agile Teams",
    image: requirementsListShot,
    screenshot: true,
    imageHeight: 700,
    imageAlt: "Requirement list for a project with status, assignee and FrothAI actions, plus Module and Build tabs",
    paragraphs: [
      "In fast-moving Agile teams, requirements change quickly and misalignment between QA and development slows everyone down. Froth TestOps keeps requirements clear, traceable and connected to testing.",
      "Create requirements or import them, organise parent and child requirements, transfer them between builds, and sync them two-way with Jira, Plane or Azure DevOps.",
      "Describe a requirement, or let FrothAI draft one for you. Then choose what to generate (functional, negative, edge, security, UI/UX and more), review the suggested scenarios and test cases, and insert only the ones you want.",
    ],
    bullets: ["AI-drafted requirements", "14 test generation criteria", "Review before anything is added"],
    faqs: [
      {
        q: "Requirement Traceability",
        a: "Link every requirement to its test cases and defects, and see what was built, tested and failed in one place.",
      },
      {
        q: "Import & Organize",
        a: "Import requirements from spreadsheets or sync them from Jira, Plane or Azure DevOps, or create new ones in moments. Organise them by build, module and parent requirement.",
      },
      {
        q: "Align Change Management",
        a: "Requirements changed mid-sprint? Update, reassign and realign them with downstream test cases and defects in a few clicks.",
      },
    ],
  },
  {
    id: "build-management",
    label: "Build Management",
    icon: Boxes,
    title: "Simplify Build Management with Froth TestOps",
    paragraphs: [
      "Without proper build management, development can become fragmented and error-prone. Froth TestOps tracks every build and the testing done against it, so you know the quality of each release.",
      "Froth TestOps links test executions and defects to each build, triggers automation through your GitHub or GitLab runners, and shows live execution status.",
    ],
    cards: [
      {
        icon: Link2,
        title: "Link bugs and test executions to builds",
        text: "Trace every issue to the right release and confirm the most important tests ran before deployment.",
      },
      {
        icon: CalendarClock,
        title: "Automate test execution for each build",
        text: "Schedule and automate test runs per build so problems are caught early in the release.",
      },
      {
        icon: Clock,
        title: "Release Timeline",
        text: "See planned, active and completed builds on a timeline, with an overview of each build.",
      },
    ],
    faqs: [
      {
        q: "What is Build Management?",
        a: "Build management is the process of building, testing and deploying software in a controlled way. Froth TestOps tracks builds and connects them to your test runs, defects and CI runners.",
      },
      {
        q: "Why is Build Management Important?",
        a: "It helps teams release on time and with good quality. Froth TestOps keeps build testing simple, so teams can focus on delivering better products faster.",
      },
      {
        q: "What is a Build in the SDLC?",
        a: "A build is a compiled version of the software that is ready for testing or release. Froth TestOps makes managing builds effortless, improving the speed and quality of the whole development cycle.",
      },
    ],
  },
  {
    id: "integrations",
    label: "Integrations",
    icon: Plug,
    title: "Seamless Integrations for a Smarter Workflow",
    paragraphs: [
      "Froth TestOps works with the tools you already use: Jira, Plane and Azure DevOps for requirements and defects; GitHub and GitLab for repositories, CI runners and code scanning; BrowserStack for real devices and browsers; and Azure AD for single sign-on.",
      "Automate repetitive tasks, keep changes in sync and make sure every release meets your quality standards, from one platform.",
    ],
    cards: integrations.map(item => ({
      icon: () => <IntegrationLogo item={item} />,
      title: item.name,
      text: item.text,
    })),
    faqs: [
      {
        q: "Which tools does Froth TestOps integrate with?",
        a: "Jira, Plane and Azure DevOps for requirements and defects; GitHub and GitLab for repositories, CI runners and code scanning; BrowserStack for real devices and browsers; and Azure AD for single sign-on.",
      },
      {
        q: "Do integrations sync both ways?",
        a: "Yes. Requirements and defects sync two-way with Jira, Plane and Azure DevOps, with custom field and user mapping, so changes made in either tool stay current.",
      },
      {
        q: "Do I need to install anything to connect a tool?",
        a: "No. Connect your tools from Settings → Integration in a few clicks. For automation, connect your own GitHub Actions or GitLab runners and BrowserStack credentials.",
      },
    ],
  },
  {
    id: "release-management",
    label: "Release Management",
    icon: Rocket,
    title: "Efficient Release Management for High-Performing Software Teams",
    paragraphs: [
      "Managing several releases can get confusing. Froth TestOps puts you in charge of the whole release process, from coordinating builds and requirements to test plans and approvals, in one platform.",
      "Each test plan moves through review and sign-off, shows whether it's eligible for deployment, and produces a Test Plan Summary Report with a FrothAI release-readiness assessment and ISO 27001 controls mapping, ready for audit.",
      "Run tests, monitor progress and generate reports for each build separately, keeping your projects structured and your releases on plan.",
    ],
    cards: [
      {
        icon: CheckCircle2,
        title: "Review and sign-off",
        text: "Send for review, mark reviewed, send for sign-off and approve, with full review and sign-off history.",
      },
      {
        icon: FrothAiIcon,
        accent: true,
        title: "Release readiness",
        text: "A FrothAI analysis and risk assessment in every Test Plan Summary Report.",
      },
      {
        icon: FileBarChart,
        title: "Audit-ready reports",
        text: "Download the summary as a PDF with coverage, results, defects, evidence, approvals and ISO 27001 mapping.",
      },
    ],
    faqs: [
      {
        q: "What is Release Management?",
        a: "Release management coordinates builds, requirements, test plans and approvals so software ships on time and to standard. Froth TestOps manages the whole process in one platform.",
      },
      {
        q: "How do I know a release is ready?",
        a: "Every test plan shows its review and sign-off status, and the Test Plan Summary Report includes a FrothAI release-readiness assessment with risk analysis and ISO 27001 controls mapping.",
      },
      {
        q: "Can releases be audited?",
        a: "Yes. Download the Test Plan Summary Report as a PDF with coverage, results, defects, evidence, approvals and ISO 27001 mapping, ready for audit.",
      },
    ],
  },
  {
    id: "collaborative-test-design",
    label: "Collaborative Test Design",
    icon: Users,
    title: "Collaborative Test Design",
    paragraphs: [
      "With Froth TestOps, the whole QA workflow lives in one place. There's no need to switch between email, tools or spreadsheets. Plan and organise test cycles, create and track test plans, and manage approvals and sign-offs on the platform itself.",
      "Developers, testers and business teams work together in a shared workspace, and every decision leaves a clear audit trail.",
    ],
    cards: [
      {
        icon: CheckCircle2,
        title: "Review & Approve with Ease",
        text: "Test managers review and approve test cases in one Approvals Center before they're executed.",
      },
      {
        icon: MessagesSquare,
        title: "Discuss in context",
        text: "Comment and @mention teammates on test cases and defects.",
      },
      {
        icon: Bell,
        title: "Stay in the loop",
        text: "Choose which of about 20 events send email notifications.",
      },
    ],
    faqs: [
      {
        q: "How do teams collaborate on test design?",
        a: "Test planning, cycles, approvals and sign-off all happen on the platform, so there's no switching between email, tools or spreadsheets. Developers, testers and business teams work in one shared workspace.",
      },
      {
        q: "Can teammates discuss a test case or defect?",
        a: "Yes. Comment and @mention teammates directly on test cases and defects, so feedback stays in context and every decision leaves a clear audit trail.",
      },
      {
        q: "Will I know when something needs my attention?",
        a: "Choose which of about 20 events send email notifications — approvals, assignments, failed runs and more — so nothing slips by.",
      },
    ],
  },
  {
    id: "test-automation",
    label: "Test Automation",
    icon: Bot,
    title: "Automation Without the Overhead",
    image: automationShot,
    screenshot: true,
    imageHeight: 500,
    imageAlt: "Create Automation Script screen with Record Manually and Generate with FrothAI options",
    paragraphs: [
      "Create automation scripts three ways: record them in the browser with the TestOps Recorder, let FrothAI generate them from approved test cases (Playwright for web, Appium for Android and iOS), or write them in the built-in editor.",
      "Group scripts into suites, connect your own GitHub Actions or GitLab runners, and run on BrowserStack devices.",
    ],
    cards: [
      {
        icon: Video,
        title: "Record",
        text: "Record your actions in the browser and turn them into an automation script, with selector review for every step.",
      },
      {
        icon: Wand2,
        accent: true,
        title: "Generate with FrothAI",
        badge: <BetaBadge />,
        text: "FrothAI reads your test case steps, maps them to selectors and writes the script.",
      },
      {
        icon: PlayCircle,
        title: "Run Test Live",
        text: "Replay a script in a real browser with step screenshots, and ask FrothAI to change it if needed.",
      },
      {
        icon: Cpu,
        title: "Agents & Capabilities",
        text: "Set up GitHub Actions or GitLab runners for macOS, Linux or Windows, and generate BrowserStack capabilities.",
      },
      {
        icon: Database,
        title: "Test Data & DB Integration",
        text: "Manage key-value test data sets and run database queries inside your scripts.",
      },
      {
        icon: WorkflowIcon,
        title: "Suites & Cycles",
        text: "Group scripts into suites and run them in automation cycles with live status, logs and reruns.",
      },
    ],
    faqs: [
      {
        q: "Do I need coding skills to automate tests?",
        a: "No. Record scripts in the browser with the TestOps Recorder, or let FrothAI generate Playwright or Appium scripts from approved test cases. If you prefer to code, there's a built-in editor too.",
      },
      {
        q: "Which automation frameworks are supported?",
        a: "Playwright for web and Appium for Android and iOS. Scripts run on your own GitHub Actions or GitLab runners, or on BrowserStack real devices and browsers.",
      },
      {
        q: "Can automation run on a schedule?",
        a: "Yes. Group scripts into suites and run them in automation cycles with live status, logs and reruns, or trigger them from your CI pipeline.",
      },
    ],
  },
  {
    id: "task-management",
    label: "Task Management",
    icon: KanbanSquare,
    title: "Plan and Track QA Work",
    image: kanbanShot,
    screenshot: true,
    imageHeight: 650,
    imageAlt: "Task Management Kanban board with To Do, In Progress, In Review and Done columns",
    paragraphs: [
      "Keep every QA task visible on a Kanban board or list: development, documentation, DevOps, performance, security and more.",
      "Link tasks to requirements, defects, test cases and test plans, save your favourite views, and let FrothAI break a description down into tasks and activities.",
    ],
    bullets: [
      "Kanban and list views with drag and drop",
      "Saved views for the filters you use most",
      "Tasks linked to requirements, defects, test cases and test plans",
      "FrothAI task generation",
    ],
    faqs: [
      {
        q: "What is Task Management in Froth TestOps?",
        a: "A Kanban board and list view for every QA task — development, documentation, DevOps, performance, security and more — with each task linked to the requirements, defects, test cases and test plans it belongs to.",
      },
      {
        q: "Can FrothAI create tasks for me?",
        a: "Yes. Describe the work and FrothAI breaks it down into tasks and activities you can review and add to the board.",
      },
      {
        q: "Can I save the views I use most?",
        a: "Yes. Save your favourite filters as views, and switch between Kanban and list with drag and drop.",
      },
    ],
  },
  {
    id: "dashboards-reports",
    label: "Dashboards & Reports",
    icon: BarChart3,
    title: "Know Where Every Release Stands",
    image: dashboardShot,
    screenshot: true,
    imageHeight: 562,
    imageAlt: "Project dashboard with KPI cards and testing progress",
    paragraphs: [
      "The project dashboard shows requirements, test management, automation, module and defect analytics in one place, and every chart downloads as PNG, SVG, PDF or CSV.",
      "Build your own defect, test case, execution and traceability reports, compare cycles side by side, and schedule reports to your team.",
    ],
    bullets: [
      "Six analytics tabs on the project dashboard",
      "Report builder with saved templates",
      "Cycle comparison reports",
      "Scheduled email reports in CSV or Excel",
    ],
    cards: [
      {
        icon: FileInput,
        title: "Requirement traceability",
        text: "See which requirements are covered by tests, which have defects and which still need work.",
      },
      {
        icon: BarChart3,
        title: "Cycle comparison",
        text: "Compare test cycles side by side and export the result as a PDF.",
      },
      {
        icon: CalendarClock,
        title: "Scheduled reports",
        text: "Send reports to your team daily, weekly, bi-weekly or monthly.",
      },
    ],
    faqs: [
      {
        q: "What dashboards does Froth TestOps include?",
        a: "The project dashboard covers requirements, test management, automation, module and defect analytics in one place, and every chart downloads as PNG, SVG, PDF or CSV.",
      },
      {
        q: "Can I build custom reports?",
        a: "Yes. Build defect, test case, execution and traceability reports with filters for status, severity, coverage and more, save them as templates, and compare test cycles side by side.",
      },
      {
        q: "Can reports be sent automatically?",
        a: "Yes. Schedule reports to your team daily, weekly, bi-weekly or monthly by email, in CSV or Excel.",
      },
    ],
  },
];

const WorkFlow = () => {
  return (
    <>
      <SEOHead
        title="QA Workflow Automation for Faster Testing and Better QA"
        description="Simplify and automate testing with our qa workflow automation tools, boosting testing speed, accuracy, and overall efficiency in software quality."
        keywords="qa workflow automation, test management workflow, Froth TestOps"
      />
      <div className="fd-main-wrapper page-padding">
        <Header />
        <main>
          <PageHero
            crumbs="Solutions by Workflow"
            eyebrow="Solutions"
            title={
              <>
                Solutions <span>by Workflow</span>
              </>
            }
            lead="From requirements to release sign-off, see how Froth TestOps supports every step of your QA workflow."
            visual={
              <HeroShots
                main={kanbanShot}
                mainAlt="Froth TestOps task Kanban board"
                mainHeight={650}
                float={requirementsShot}
                floatAlt="Generating requirements with FrothAI in Froth TestOps"
                floatHeight={662}
              />
            }
          />
          <SolutionShowcase tabs={tabs} label="Workflows" />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default WorkFlow;
